import React, { useEffect, useMemo, useState } from 'react';

import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import SignIn from './features/login/SignIn';
import App from './App';
import awsconfig from './aws-exports';
import { useRecoilValue } from 'recoil';
import { IonContent, IonPage } from '@ionic/react';
import useToastPresenter from './utils/useToastPresenter';
import { _datasource } from './utils/state/model/implementations/ImplementationFactory';
import { logFriendlyObject } from '@otuvy/common';
import { isPermittedNetworkConnectedState } from './sync/network/network';
import { AuthCache, isCurrentUserAuthenticated, UserPropertyKeys } from '@otuvy/auth';
import * as Sentry from '@sentry/capacitor';
import { QrActionTypes, QrObjectTypes } from './constants/constants';
import PublicReportPage from './pages/PublicReportPage';
import { Capacitor } from '@capacitor/core';

Amplify.configure(awsconfig); //in order to implement basic GDPR compliance by changing Regions the data is stored in we can pass in different configurations to this (one for US or one for Europe, etc).
//Amplify.Logger.LOG_LEVEL = 'DEBUG'

enum LoginStatus {
  LOADING,
  LOGGED_IN,
  NOT_LOGGED_IN,
}

/**
 * Checks if the current URL is a public report route (/qr/sh/list/:id)
 * Returns the list ID if it is, null otherwise
 *
 * Returns null (skips interception) when:
 * - Running on native platform (Capacitor) - in-app QR scans should use the share flow
 * - ?internal=true is present - user is being routed to share flow
 */
const getPublicReportListID = (): string | null => {
  // Skip public report interception on native platforms
  // In-app QR scans should use the share flow, not public report
  if (Capacitor.isNativePlatform()) {
    return null;
  }

  const path = window.location.pathname;
  const search = window.location.search;

  // Skip interception if internal=true (user is being routed to share flow)
  if (search.includes('internal=true')) {
    return null;
  }

  // Match /qr/sh/list/:id pattern
  const match = path.match(/^\/qr\/([^/]+)\/([^/]+)\/([^/]+)$/);
  if (match) {
    const [, action, idType, id] = match;
    if (action === QrActionTypes.SHARE && idType === QrObjectTypes.LIST) {
      return id;
    }
  }
  return null;
};

const SecureApp: React.FC = () => {
  // Check if this is a public report route - these are accessible without authentication
  // This check must be done first, before any auth-related hooks
  const publicReportListID = useMemo(() => getPublicReportListID(), []);

  // If this is a public report route, render it directly without auth
  // Using a separate component to avoid hooks rules violations
  if (publicReportListID) {
    return <PublicReportPage listID={publicReportListID} />;
  }

  // For authenticated routes, use the full SecureApp with auth logic
  return <AuthenticatedApp />;
};

const AuthenticatedApp: React.FC = () => {
  const [loginStatus, setLoginStatus] = useState<LoginStatus>(LoginStatus.LOADING);
  const isOnline = useRecoilValue(isPermittedNetworkConnectedState);
  useToastPresenter();

  useEffect(() => {
    updateLoginStatus();

    const callBackToStopListener = Hub.listen('auth', async (data) => {
      console.log(`Detected auth event at ${new Date()}`, data);
      switch (
        data.payload.event // See https://docs.amplify.aws/react/build-a-backend/auth/auth-events/#listen-to-and-log-auth-events for list of auth events
      ) {
        case 'signedIn':
          await updateLoginStatus(); //Once they log in we want to "freshen" our status flags
          await _datasource.postSignIn();
          break;
        case 'signedOut':
          AuthCache.invalidateCache();
          await _datasource.postSignOut(); //the best practice is to clear the local database on logout in the auth event listener
          await updateLoginStatus();
          break;
        case 'tokenRefresh':
          //Huzzah on refreshing the token.  There is nothing to do about it, except update the user's status
          await updateLoginStatus();
          break;
        case 'tokenRefresh_failure':
          if (loginStatus === LoginStatus.NOT_LOGGED_IN) {
            //It looks like we are getting these errors on the sign-in page, which is ok when there is not a current user/session to refresh
          } else {
            console.error('Signed in but also token refresh failure');
            //TODO: Find out what is causing these and whether it is critical.  They may be harmless
          }
          break;
        case 'signInWithRedirect':
          break;
        case 'signInWithRedirect_failure':
          break;
        case 'customOAuthState':
          break;
        default:
          console.warn('Detected unhandled Authentication event', data);
          break;
      }
    });

    return () => {
      callBackToStopListener();
    };
  }, []);

  useEffect(() => {
    if (!isOnline) {
      _datasource.onOffline();
    } else {
      _datasource.onOnline();
    }
  }, [isOnline]);

  const updateLoginStatus = async () => {
    try {
      if (!(await isCurrentUserAuthenticated())) {
        setLoginStatus(LoginStatus.NOT_LOGGED_IN);
        return;
      }

      await AuthCache.refresh();

      // Attach Sentry user context so subsequent error events are scoped to this user.
      // Cleared in src/utils/auth/authTearDown.ts on sign-out. Swallow any failure -
      // diagnostic only, must not block sign-in.
      try {
        const userId = AuthCache.getCurrentUserId();
        if (userId) {
          // AuthCache.getCachedUserAttribute can return string | string[] | undefined;
          // Sentry's setUser.username + setTag value want a single primitive string.
          const email = AuthCache.getCachedUserAttribute(UserPropertyKeys.email);
          const organizationId = AuthCache.getCachedUserAttribute(UserPropertyKeys.organizationId);
          Sentry.setUser({
            id: userId,
            username: typeof email === 'string' ? email : undefined,
          });
          if (typeof organizationId === 'string') Sentry.setTag('organization_id', organizationId);
        }
      } catch (e) {
        console.error('[sentry] setUser after sign-in failed', e);
      }

      await initDatabase();
      setLoginStatus(LoginStatus.LOGGED_IN);
    } catch (exception) {
      if (exception === 'The user is not authenticated') {
        setLoginStatus(LoginStatus.NOT_LOGGED_IN);
      } else if (exception === 'No current user') {
        setLoginStatus(LoginStatus.NOT_LOGGED_IN);
      } else {
        console.error(
          "Unhandled exception fetching current user's session while updating login status",
          logFriendlyObject(exception)
        );
        setLoginStatus(LoginStatus.NOT_LOGGED_IN);
      }
    }
  };

  const initDatabase = async () => {
    await _datasource.init();
  };

  const retComponent = (): JSX.Element => {
    switch (loginStatus) {
      case LoginStatus.LOADING:
        return <></>;
      case LoginStatus.NOT_LOGGED_IN:
        return <SignIn />;
      default:
        return <App />;
    }
  };

  return (
    <IonPage>
      <IonContent>{retComponent()}</IonContent>
    </IonPage>
  );
};

export default SecureApp;
